import React from 'react';
import { Modal, Table, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import './RiskDropConfirmationModal.sass';

interface RiskDropConfirmationModalProps {
    visible: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    item: {
        name: string;
        impact: number;
        likelihood: number;
        isOpportunity?: boolean;
    };
    newPosition: {
        impact: number;
        likelihood: number;
    };
    scenarioName: string;
    impacts: any[] | { [key: string]: any };
    likelihoods: any[] | { [key: string]: any };
}

export const RiskDropConfirmationModal: React.FC<RiskDropConfirmationModalProps> = ({
    visible,
    onConfirm,
    onCancel,
    item,
    newPosition,
    scenarioName,
    impacts,
    likelihoods
}) => {
    const { t } = useTranslation();

    const getImpactName = (impactValue: number) => {
        if (!impacts) {
            return impactValue.toString();
        }

        // Handle both array and object formats
        let impact;
        if (Array.isArray(impacts)) {
            impact = impacts.find(i => parseInt(i.id) === impactValue);
        } else {
            // Handle indexed object format - try both integer and decimal keys
            impact = impacts[impactValue.toString()] || impacts[Math.floor(impactValue).toString()];
        }

        return impact ? `${impactValue} - ${impact.name}` : impactValue.toString();
    };

    const getLikelihoodName = (likelihoodValue: number) => {
        if (!likelihoods) {
            return likelihoodValue.toString();
        }

        // Handle both array and object formats
        let likelihood;
        if (Array.isArray(likelihoods)) {
            likelihood = likelihoods.find(l => parseInt(l.id) === likelihoodValue);
        } else {
            // Handle indexed object format - try both integer and decimal keys
            likelihood = likelihoods[likelihoodValue.toString()] || likelihoods[Math.floor(likelihoodValue).toString()];
        }

        return likelihood ? `${likelihoodValue} - ${likelihood.name}` : likelihoodValue.toString();
    };

    const columns = [
        {
            title: '',
            dataIndex: 'field',
            key: 'field',
            width: 100,
        },
        {
            title: t('actions.confirm_drop.before'),
            dataIndex: 'before',
            key: 'before',
            width: 150,
        },
        {
            title: t('actions.confirm_drop.after'),
            dataIndex: 'after',
            key: 'after',
            width: 150,
        },
    ];

    const data = [
        {
            key: 'likelihood',
            field: t('actions.confirm_drop.likelihood'),
            before: getLikelihoodName(item.likelihood),
            after: getLikelihoodName(newPosition.likelihood),
            // Align left
            align: 'left',
        },
        {
            key: 'impact',
            field: t('actions.confirm_drop.impact'),
            before: getImpactName(item.impact),
            after: getImpactName(newPosition.impact),
            align: 'left',
        },
    ];

    const footer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={onCancel}>
                {t('actions.cancel')}
            </Button>
            <Button type="primary" onClick={onConfirm}>
                {t('actions.save')}
            </Button>
        </div>
    );

    // Determine if we're on baseline or a named scenario
    const isBaseline = scenarioName === t('models.scenario.baseline') || scenarioName === 'Baseline';
    const titleKey = isBaseline ? 'actions.confirm_drop.message_baseline' : 'actions.confirm_drop.message_scenario';
    const title = isBaseline ? t(titleKey) : t(titleKey, { scenarioName });

    return (
        <Modal
            open={visible}
            title={title}
            onCancel={onCancel}
            footer={footer}
            maskClosable={false}
            width={550}
        >
            <div style={{ marginBottom: '16px' }}>
                <strong>{item.isOpportunity ? t('models.risk.name_positive') : t('models.risk.name')}: {item.name}</strong>
            </div>
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                size="small"
                bordered={false}
                className="risk-drop-confirmation-table"
            />
        </Modal>
    );
};

export default RiskDropConfirmationModal;
